import { isNil } from "lodash";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import useSession from "Hooks/session.hook";
import {
  createBox,
  createRack,
  createTrolley,
  createVehicle,
  fetchBox,
  fetchRack,
  listServicePoints,
  fetchTrolley,
  fetchVehicle,
  updateBox,
  updateRack,
  updateTrolley,
  updateVehicle,
  createVehicleType,
  updateVehicleType,
  fetchVehicleType,
  listVehicleTypes,
} from "Services/delivery-settings.services";
import { fetchDriverList } from "Services/new-driver-management.services";
import { decode } from "Helpers/enc";
import { toaster } from "Helpers/toaster";
import { DriverApprovalStatus } from "Types/new_driver_types";
import { StackScreen } from "@life-line-lab/components-react";
import {
  BoxForm,
  RackForm,
  TrolleyForm,
  VehicleForm,
} from "Components/delivery-management/delivery-forms";
import VehicleTypeForm from "Components/delivery-management/delivery-forms/vehicle-type-form";

const DeliveryAssetsForm: React.FC<any> = () => {
  const history = useHistory();
  const { org } = useSession();
  const { t } = useTranslation();
  const { type } = useParams<{ type: string }>();

  const [settings, setSettings] = useState<any>({});
  const [response, setResponse] = useState<any>([]);
  const [activeForm, setActiveForm] = useState(<></>);

  useEffect(() => {
    if (type) {
      switch (type) {
        case "0":
          setActiveForm(
            <RackForm
              onSubmit={handleCreateRack}
              onUpdate={handleUpdateRack}
              onFetch={handleFetchRack}
              onFetchServicePoints={handleFetchServicePoints}
              data={response}
              settings={settings}
            />,
          );
          break;
        case "1":
          setActiveForm(
            <BoxForm
              onSubmit={handleCreateBox}
              onUpdate={handleUpdateBox}
              onFetch={handleFetchBox}
              data={response}
            />,
          );
          break;
        case "2":
          setActiveForm(
            <TrolleyForm
              onSubmit={handleCreateTrolley}
              onUpdate={handleUpdateTrolley}
              onFetch={handleFetchTrolley}
              data={response}
            />,
          );
          break;
        case "3":
          setActiveForm(
            <VehicleTypeForm
              onSubmit={handleCreateVehicleType}
              onUpdate={handleUpdateVehicleType}
              onFetch={handleFetchVehicleType}
              data={response}
            />,
          );
          break;
        case "4":
          setActiveForm(
            <VehicleForm
              onSubmit={handleCreateVehicle}
              onUpdate={handleUpdateVehicle}
              onFetch={handleFetchVehicle}
              onFetchOptions={handleFetchVehicleFormSettings}
              data={response}
              settings={settings}
            />,
          );
          break;
        default:
      }
    }
  }, [type, response, settings]);

  const handleFetchVehicleFormSettings = async () => {
    const responseData = await Promise.all([
      listVehicleTypes(),
      fetchDriverList({}, [org._id], DriverApprovalStatus.APPROVED),
    ]);

    const errorExists = responseData.find((r) => r.error);

    if (!errorExists) {
      // there must be exactly 2 arrays in responseData
      setSettings((prev) => ({
        ...prev,
        VEHICLE_TYPE: responseData[0],
        DRIVER: responseData[1],
      }));
    }
  };

  const handleCreateRack = async (data: any) => {
    data.active = true;
    data.orgID = org._id;
    let responseData = await createRack(data);
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.createSuccessful"));
      history.push(`/delivery-management/settings/assets?tabKey=${type}`);
    } else {
      toaster("error", t("toaster.createFailed"));
    }
  };

  const handleUpdateRack = async (data: any) => {
    data.active = response.active;
    data.orgID = org._id;
    let responseData = await updateRack(data);
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.updateSuccessful"));
      history.push(`/delivery-management/settings/assets?tabKey=${type}`);
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleFetchRack = async (pointTypeID: string) => {
    const responseData = await fetchRack(decode(pointTypeID) as string);
    if (!isNil(responseData)) {
      setResponse(responseData);
    }
  };

  const handleCreateBox = async (data: any) => {
    data.active = true;
    data.orgID = org._id;
    let responseData = await createBox(data);
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.createSuccessful"));
      history.push(`/delivery-management/settings/assets?tabKey=${type}`);
    } else {
      toaster("error", t("toaster.createFailed"));
    }
  };

  const handleUpdateBox = async (data: any) => {
    data.active = response.active;
    data.orgID = org._id;
    let responseData = await updateBox(data);
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.updateSuccessful"));
      history.push(`/delivery-management/settings/assets?tabKey=${type}`);
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleFetchBox = async (pointTypeID: string) => {
    const responseData = await fetchBox(decode(pointTypeID) as string);
    if (!isNil(responseData)) {
      setResponse(responseData);
    }
  };

  const handleCreateTrolley = async (data: any) => {
    data.active = true;
    data.orgID = org._id;
    let responseData = await createTrolley(data);
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.createSuccessful"));
      history.push(`/delivery-management/settings/assets?tabKey=${type}`);
    } else {
      toaster("error", t("toaster.createFailed"));
    }
  };

  const handleUpdateTrolley = async (data: any) => {
    data.active = response.active;
    data.orgID = org._id;
    let responseData = await updateTrolley(data);
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.updateSuccessful"));
      history.push(`/delivery-management/settings/assets?tabKey=${type}`);
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleFetchTrolley = async (pointTypeID: string) => {
    const responseData = await fetchTrolley(decode(pointTypeID) as string);
    if (!isNil(responseData)) {
      setResponse(responseData);
    }
  };

  const handleCreateVehicle = async (data: any) => {
    data.active = true;
    data.orgID = org._id;
    let responseData = await createVehicle(data);
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.createSuccessful"));
      history.push(`/delivery-management/settings/assets?tabKey=${type}`);
    } else {
      toaster("error", t("toaster.createFailed"));
    }
  };

  const handleUpdateVehicle = async (data: any) => {
    data.active = response.active;
    data.orgID = org._id;
    let responseData = await updateVehicle(data);
    if (!isNil(responseData) && responseData.success) {
      toaster("success", t("toaster.updateSuccessful"));
      history.push(`/delivery-management/settings/assets?tabKey=${type}`);
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleFetchVehicleTypeList = async () => {
    const responseData = await listVehicleTypes();
    if (!isNil(responseData)) {
      setSettings((prev) => ({ ...prev, VEHICLE_TYPES: responseData }));
    }
  };

  const handleFetchVehicleType = async (vehicleTypeID: string) => {
    const responseData = await fetchVehicleType(
      decode(vehicleTypeID) as string,
    );
    if (!isNil(responseData)) {
      setResponse(responseData);
    }
  };

  const handleCreateVehicleType = async (data: any) => {
    const payload = {
      ...data,
      active: true,
      orgID: org?._id,
    };

    const responseData: any = await createVehicleType(payload);

    if (!responseData.error) {
      toaster("success", t("toaster.createSuccessful"));
      history.push(`/delivery-management/settings/assets?tabKey=${type}`);
    } else {
      toaster("error", t("toaster.createFailed"));
    }
  };

  const handleUpdateVehicleType = async (data: any) => {
    const payload = {
      ...data,
      active: true,
      orgID: org?._id,
    };

    const responseData: any = await updateVehicleType(payload);

    if (!responseData.error) {
      toaster("success", t("toaster.createSuccessful"));
      history.push(`/delivery-management/settings/assets?tabKey=${type}`);
    } else {
      toaster("error", t("toaster.createFailed"));
    }
  };

  const handleFetchVehicle = async (pointTypeID: string) => {
    const responseData = await fetchVehicle(decode(pointTypeID) as string);
    if (!isNil(responseData)) {
      setResponse(responseData);
    }
  };

  const handleFetchServicePoints = async () => {
    const responseData = await listServicePoints({active: true});
    if (responseData && !responseData.error) {
      setSettings((settings) => {
        return { ...settings, SERVICE_POINT: responseData };
      });
    }
  };

  const handleFetchDrivers = async () => {
    const responseData = await fetchDriverList(
      {},
      [org._id],
      DriverApprovalStatus.APPROVED,
    );
    if (responseData && !responseData.error) {
      setSettings((settings) => {
        return { ...settings, DRIVER: responseData };
      });
    }
  };

  const handleCloseStackScreen = () => {
    history.push(`/delivery-management/settings/assets?tabKey=${type}`);
  };

  return (
    <StackScreen
      title={
        <div>
          <img
            alt={t("alt.logo")}
            src="/assets/images/banner_logo.png"
            height={30}
          />
        </div>
      }
      isOpen={true}
      renderHeader={<div></div>}
      onClose={handleCloseStackScreen}
    >
      <Helmet>
        <title></title>
      </Helmet>

      {activeForm}
    </StackScreen>
  );
};

export default DeliveryAssetsForm;
